/**
 * Avatar 컴포넌트
 * @description 사람·대상을 나타내는 상자. `type` 이 무엇을 그릴지 정하고, 내용은 짝이 되는
 *   prop 으로 받는다. 누름·배지·태그·선택은 감싸는 컴포넌트의 몫이다. 앱 DS `Avatar` 의
 *   웹 포팅.
 * @example
 * <Avatar type='image' image={<AppImage uri={user.profileImageUrl} />} accessibilityLabel={user.name} />
 * <Avatar type='initial' initial='NY' size='sm' />
 * <Avatar type='icon' icon={Lock} radius='square' />
 */
import { memo } from "react";

import {
  AVATAR_ICON_COLOR,
  AVATAR_ICON_SIZE,
  avatarStyles,
} from "./Avatar.styles";
import type { AvatarProps } from "./Avatar.types";

import { iconStrokeWidth } from "../../../icons/iconStroke";

const Avatar = memo<AvatarProps>((props) => {
  const {
    size = "md",
    radius = "circle",
    tone = "neutral",
    accessibilityLabel,
    testID,
  } = props;
  const iconSize = AVATAR_ICON_SIZE[size];

  return (
    <span
      role={accessibilityLabel ? "img" : undefined}
      aria-label={accessibilityLabel}
      aria-hidden={accessibilityLabel ? undefined : true}
      className={avatarStyles.root({ tone, size, radius })}
      data-testid={testID}
    >
      {props.type === "image" ? (
        <span className={avatarStyles.image}>{props.image}</span>
      ) : null}
      {props.type === "icon" ? (
        <props.icon
          color={AVATAR_ICON_COLOR[tone]}
          size={iconSize}
          strokeWidth={iconStrokeWidth(iconSize)}
        />
      ) : null}
      {props.type === "initial" ? (
        <span className={avatarStyles.initial({ tone, size })}>
          {props.initial}
        </span>
      ) : null}
    </span>
  );
});

Avatar.displayName = "Avatar";

export default Avatar;
